import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Loader from '../reusable/Loader';
import TableSkeleton from '../reusable/TableSkeleton';
import Panel from '../reusable/Panel';
import useUserDetails from '../hooks/useUserDetails';
import useSchoolReportStatusChannel from '../hooks/useSchoolReportStatusChannel';
import { getConfiguredSchools, getEOAConfig } from '../../services/eoaConfigServices';
import { getWorkflowStatuses, WorkflowTransitionResponse } from '../../services/workflowService';
import { ConfiguredEOASchool } from '../../interfaces/EOAInterface';
import { USER_ROLES } from '../../constants';
import SchoolReportStatus from './SchoolReportStatus';
import SchoolReportWorkflowModal from './SchoolReportWorkflowModal';
import ReportSchoolReportIssueModal from './ReportSchoolReportIssueModal';
import ApproveSchoolReportModal from './ApproveSchoolReportModal';
import ViewSchoolReportLink from './ViewSchoolReportLink';
import GoBackIcon from '../assets/transcript-selection-arrow-left.svg';
import './SchoolReportsOverview.css';
import '../data qc/ReportIssueModal.css';
import '../data qc/ApproveDataQcModal.css';
import { formatDate } from '../../utils';

interface SchoolWithStatus {
  id: string;
  name: string;
  status: string;
  dueDate: string;
  actions: JSX.Element;
}

const SchoolReportsOverview = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState<SchoolWithStatus[]>([]);
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
  const [isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('');
  const [selectedSchoolStatus, setSelectedSchoolStatus] = useState<string>('');

  const shouldShowActions = (status: string): boolean => {
    const userRole = userDetails?.role;

    if (userRole === USER_ROLES.LEAD_FACILITATOR) {
      return status === 'SCHOOL_REPORT_READY_FOR_INTERNAL_REVIEW';
    }

    if (userRole === USER_ROLES.XQ_ADMIN) {
      const showActionsStatuses = [
        'SCHOOL_REPORT_READY_FOR_INTERNAL_REVIEW',
        'SCHOOL_REPORT_APPROVED_BY_LEAD_FACILITATOR',
        'SCHOOL_REPORT_READY_FOR_DISTRICT_REVIEW',
      ];
      return showActionsStatuses.includes(status);
    }

    return false;
  };

  const shouldShowDownloadLink = (status: string): boolean => {
    const downloadableStatuses = [
      'SCHOOL_REPORT_GENERATED',
      'SCHOOL_REPORT_READY_FOR_INTERNAL_REVIEW',
      'SCHOOL_REPORT_APPROVED_BY_LEAD_FACILITATOR',
      'SCHOOL_REPORT_READY_FOR_DISTRICT_REVIEW',
      'SCHOOL_REPORT_FINALIZED',
    ];
    return downloadableStatuses.includes(status);
  };

  const handleRejectClick = (schoolId: string, status: string) => {
    setSelectedSchoolId(schoolId);
    setSelectedSchoolStatus(status);
    setIsReportIssueModalOpen(true);
  };

  const handleApproveClick = (schoolId: string, status: string) => {
    setSelectedSchoolId(schoolId);
    setSelectedSchoolStatus(status);
    setIsApproveModalOpen(true);
  };

  const renderActionButtons = (schoolId: string, status: string) => {
    const showDownload = shouldShowDownloadLink(status);
    const showActions = shouldShowActions(status);

    if (!showDownload && !showActions) {
      return <span></span>;
    }

    return (
      <div className="school-report-actions">
        {showDownload && <ViewSchoolReportLink schoolId={schoolId} />}
        {showActions && (
          <>
            <button className="school-report-action-link" onClick={() => handleApproveClick(schoolId, status)}>
              Approve
            </button>
            <button className="school-report-action-link" onClick={() => handleRejectClick(schoolId, status)}>
              Reject
            </button>
          </>
        )}
      </div>
    );
  };

  const fetchSchoolsAndStatus = async ({ showLoading = true } = {}) => {
    if (!userDetails) return;

    try {
      if (showLoading) {
        setLoading(true);
      }

      const eoaConfigResponse = await getEOAConfig(userDetails.eoaId, userDetails.access_token);
      const sjwDate = eoaConfigResponse.data.sjw_date;

      const formattedDueDate = formatDate(sjwDate);

      const schoolsResponse = await getConfiguredSchools(userDetails.eoaId, userDetails.access_token);
      const configuredSchools = schoolsResponse.data;
      const primarySchools = configuredSchools.filter(
        (school: ConfiguredEOASchool) => school.is_primary_on_eoa !== false
      );

      let workflowStatuses: Record<string, { school_report: string | null }> = {};
      try {
        const statusesResponse = await getWorkflowStatuses(userDetails.eoaId, userDetails.access_token);
        workflowStatuses = statusesResponse.schools;
      } catch (error) {
        console.error('Error fetching bulk workflow statuses, using default statuses:', error);
      }

      const schoolsWithStatus = primarySchools.map((school: ConfiguredEOASchool) => {
        const status = workflowStatuses[school.id]?.school_report || 'SCHOOL_REPORT_NOT_GENERATED';

        return {
          id: school.id,
          name: school.name,
          status: status,
          dueDate: formattedDueDate,
          actions: renderActionButtons(school.id, status),
        };
      });

      setSchools(schoolsWithStatus);
    } catch (error) {
      console.error('Error fetching schools and status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchoolsAndStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails]);

  useSchoolReportStatusChannel(() => {
    fetchSchoolsAndStatus({ showLoading: false });
  });

  const handleLearnMoreClick = () => {
    setIsWorkflowModalOpen(true);
  };

  const handleCloseWorkflowModal = () => {
    setIsWorkflowModalOpen(false);
  };

  const updateSchoolStatus = (transitionResponse: WorkflowTransitionResponse) => {
    setSchools((prevSchools) =>
      prevSchools.map((school) =>
        school.id === selectedSchoolId
          ? {
              ...school,
              status: transitionResponse.current_state,
              actions: renderActionButtons(school.id, transitionResponse.current_state),
            }
          : school
      )
    );
  };

  const handleReportIssueSuccess = (transitionResponse: WorkflowTransitionResponse) => {
    setIsReportIssueModalOpen(false);
    updateSchoolStatus(transitionResponse);
  };

  const handleApproveSuccess = (transitionResponse: WorkflowTransitionResponse) => {
    setIsApproveModalOpen(false);
    updateSchoolStatus(transitionResponse);
  };

  const tableHeaderData: TableHeaderData<SchoolWithStatus>[] = [
    { key: 'name', label: 'School', icons: ['sort'] },
    {
      key: 'status',
      label: 'Status',
      icons: ['sort'],
      renderCell: (row) => <SchoolReportStatus status={row.status} userRole={userDetails?.role} />,
    },
    { key: 'dueDate', label: 'Due date' },
    { key: 'actions', label: 'Actions' },
  ];

  if (!userDetails) return <Loader />;

  return (
    <>
      <div className="main-content">
        <CustomButton
          variant="text"
          buttonText="Back"
          icon={<img src={GoBackIcon} alt="go back" />}
          iconPosition="start"
          buttonAction={() => navigate('/overview')}
        />
        <div className="school-reports-header">
          <h1 className="school-reports-page-title">School Report</h1>
          <button className="school-reports-learn-more-link" onClick={handleLearnMoreClick}>
            How does the status work?
          </button>
        </div>
        <Panel className="school-reports-overview-panel">
          <h2 className="school-reports-panel-title">Progress status</h2>
          <CustomTable tableHeaderData={tableHeaderData} tableBodyData={schools}>
            {loading && <TableSkeleton rowsNumber={3} colsNumber={tableHeaderData.length} />}
          </CustomTable>
        </Panel>
      </div>
      <SchoolReportWorkflowModal
        isOpen={isWorkflowModalOpen}
        onClose={handleCloseWorkflowModal}
        userRole={userDetails?.role}
      />
      <ReportSchoolReportIssueModal
        isOpen={isReportIssueModalOpen}
        onClose={() => setIsReportIssueModalOpen(false)}
        onSuccess={handleReportIssueSuccess}
        schoolId={selectedSchoolId}
      />
      <ApproveSchoolReportModal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        onSuccess={handleApproveSuccess}
        schoolId={selectedSchoolId}
        currentStatus={selectedSchoolStatus}
      />
    </>
  );
};

export default SchoolReportsOverview;
